import type { Address } from '@repo/api-types'
import camelcaseKeys from 'camelcase-keys'

export type AddressSearchResponse = {
  completions: AddressSearchResult[]
  paid: boolean
  demo: boolean
  success: boolean
}

export type AddressSearchResult = {
  id: string
  fullAddress: string
  canonicalAddressId: string
}

const transform = (data: Record<string, unknown>): Record<string, unknown> => {
  return camelcaseKeys(data, { deep: true })
}

export const useAddressFinder = () => {
  const addressInfo = async (addressId: string): Promise<{ error?: string; data?: Address }> => {
    const params = new URLSearchParams({
      key: import.meta.env.VITE_ADDRESS_FINDER_KEY,
      format: 'json',
      gps: String(0),
      id: addressId,
    })
    const url = new URL(`https://api.addressfinder.io/api/au/address/info?${params.toString()}`)
    const response = await fetch(url.toString(), {
      mode: 'cors',
      headers: {
        accept: 'application/json',
      },
    })
    const data = await response.json()

    if (data) {
      const address = {
        address1: data.address_line_2 ? data.address_line_2 : data.address_line_1,
        address2: data.address_line_2 ? data.address_line_1 : '',
        suburb: data.locality_name,
        state: data.state_territory,
        postCode: data.postcode,
        addressId: data.id,
      } as Address

      return { data: address }
    }

    return { error: 'Address not found' }
  }

  const addressSearch = async (address: string): Promise<AddressSearchResponse> => {
    const params = new URLSearchParams({
      key: import.meta.env.VITE_ADDRESS_FINDER_KEY,
      format: 'json',
      gnaf: String(1),
      post_box: String(0),
      q: address,
    })
    const url = new URL(`https://api.addressfinder.io/api/au/address/autocomplete?${params.toString()}`)
    const response = await fetch(url.toString(), {
      mode: 'cors',
      headers: {
        accept: 'application/json',
      },
    })
    const data = await response.json()
    return transform(data) as unknown as AddressSearchResponse
  }

  return {
    addressInfo,
    addressSearch,
  }
}
